import { FC } from 'react'

import { FinanceFinancialDocumentFilter } from 'generated/resources/datatable.graphql'
import {
  FinancialDocumentsAmountWidgetDocument,
  FinancialDocumentsAmountWidgetQuery,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  FinanceWidget,
  SupportedCharts,
} from 'modules/finance/components/Widgets/common/FinanceWidget'
import { removeNullBetween } from 'modules/finance/components/Widgets/common/utils'
import { buildWidget } from 'modules/finance/components/Widgets/common/widgets'

interface PreparedDataArgsType {
  amountFormatter: (value: number, currency?: string) => string
}

interface QueryArgsType {
  companyId: string
}

const prepareData = (
  data: FinancialDocumentsAmountWidgetQuery,
  prepareDataArgs: PreparedDataArgsType,
  amountCurrency?: string[]
) => {
  const [firstCurrency, ...others] = amountCurrency || []
  const [firstCurrencyFromAggregates, ...othersFromAggregates] =
    data?.finance?.financialDocumentsConnection?.aggregate?.terms?.currency?.values || []

  if (!!others.length || !!othersFromAggregates.length) {
    return {
      countersChartDatas: [
        {
          label: i18n.t('finance.overview.financial_documents.total'),
          value: 0,
          formattedValue: '--',
        },
      ],
    }
  }

  const amount = Number(
    data?.finance?.financialDocumentsConnection?.aggregate?.sum?.totalAmountCents || 0
  )

  return {
    countersChartDatas: [
      {
        label: i18n.t('finance.overview.financial_documents.total'),
        value: amount,
        formattedValue: prepareDataArgs.amountFormatter(
          amount,
          firstCurrency || firstCurrencyFromAggregates
        ),
      },
    ],
  }
}

export const buildFinancialDocumentsAmountWidget = ({
  widgetTitle,
  widgetId,
  queryArgs,
  prepareDataArgs,
  filters,
}: {
  widgetTitle?: string
  widgetId: string
  queryArgs: QueryArgsType
  prepareDataArgs: PreparedDataArgsType
  filters: FinanceFinancialDocumentFilter
}) => {
  const FinancialDocumentsAmountWidget: FC<{
    data: ReturnType<typeof prepareData>
  }> = ({ data }) => {
    const { countersChartDatas } = data

    return (
      <FinanceWidget
        id={`${widgetId}Dashboard`}
        type={SupportedCharts.COUNTERS}
        showChart={false}
        countersChartDatas={countersChartDatas.map((chartData) => ({
          ...chartData,
          label: widgetTitle || chartData.label,
        }))}
      />
    )
  }

  // This is to avoid problems with date filter and between provoked by the use of datatable filters
  const filteredFinancialDocumentsFilters = removeNullBetween(filters)

  const filterCurrencies = filters.currency?.in?.flatMap((currency) => currency)

  const args = {
    companyId: queryArgs.companyId,
  }

  return buildWidget({
    id: widgetId,
    component: FinancialDocumentsAmountWidget,
    document: FinancialDocumentsAmountWidgetDocument,
    prepareData: (data: FinancialDocumentsAmountWidgetQuery) =>
      prepareData(data, prepareDataArgs, filterCurrencies),
    queryArgs: {
      ...args,
      financialDocumentsFilters: { ...filteredFinancialDocumentsFilters },
    },
  })
}
